import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, HpButton } from '@hp/ui';
import { reportClientError } from '../lib/client-errors';

interface State {
  error: Error | null;
}

/** Last line of defence for a render crash: report it, show a calm page with a reload button. */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError({
      message: error.message || error.name,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      source: 'boundary',
    });
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return <CrashCard />;
  }
}

function CrashCard() {
  const { t } = useTranslation();
  return (
    <div className="bo-crash">
      <div className="hp-card bo-crash__card">
        <EmptyState
          icon="alert"
          title={t('errors.somethingWentWrong')}
          action={
            <HpButton variant="primary" icon="refresh" onClick={() => window.location.reload()}>
              {t('actions.reload')}
            </HpButton>
          }
        >
          {t('errors.reported')}
        </EmptyState>
      </div>
    </div>
  );
}
